import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth.jsx";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("ah_token");
  if (token) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[linear-gradient(180deg,#fffdf8_0%,#f6f2e9_100%)] text-foreground"
      data-testid="login-page"
    >
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr,0.9fr]">
        <div className="hidden lg:flex border-r border-[#2b313a] bg-[radial-gradient(circle_at_top_left,rgba(197,160,89,0.16),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(197,160,89,0.08),transparent_24%),linear-gradient(180deg,#111317_0%,#181c22_100%)]">
          <div className="w-full p-12 xl:p-16 flex flex-col">

            <div className="flex-1 flex flex-col justify-center">
              <div className="max-w-2xl mx-auto w-full text-center">
                <div className="mx-auto w-full max-w-[520px] rounded-[32px] border border-[#8c6a3d]/60 bg-[#111419] px-10 py-12 xl:px-12 xl:py-14 shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
                  <img
                    src="/assets/brands/ah4.svg"
                    alt="Acharya Holdings emblem"
                    className="w-full max-w-[420px] mx-auto object-contain"
                  />
                </div>
                <div className="mt-8 text-[11px] uppercase tracking-[0.22em] text-slate-300 font-medium">
                  Internal CRM
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-10 lg:p-12">
          <div className="w-full max-w-md">

            <div className="rounded-[28px] border border-[#e7dfd2] bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)] p-7 sm:p-9">
              <div className="text-[10px] uppercase tracking-[0.22em] text-bronze font-medium">Sign in</div>
              <h2 className="font-display text-3xl font-semibold tracking-tight text-slate-900 mt-4">Welcome back</h2>
              <p className="text-sm text-slate-600 mt-2 mb-8">
                Enter your credentials to continue into the internal CRM workspace.
              </p>

              <form onSubmit={submit} className="w-full" data-testid="login-form">
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="login-email"
                      className="mt-1.5 bg-white border-border h-11 text-sm"
                      autoComplete="email"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password"
                      className="mt-1.5 bg-white border-border h-11 text-sm"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit"
                    className="w-full h-11 bg-bronze hover:bg-[#d4af68] text-black font-medium"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        Sign in
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
